import React from 'react';
import { ApolloError, type FetchResult } from '@apollo/client';
import { client } from '~/api/client';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import {
  UpdateCaraWorkspaceArtifactDocument,
  UpdateCaraWorkspaceArtifactMutation,
  UpdateCaraWorkspaceArtifactMutationVariables,
  type VisibilityConfigInput
} from '~/api/graphql';
import type { SaveStatus } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import { editorSaveContext } from '~/components/document-editor/use-editor-document-persistence';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { useArtifactEditor as useLegacyArtifactEditor } from '~/pages/copilot/workspace/legacy-editor/use-artifact-editor';

const throwOnMutationErrors = (result: FetchResult) => {
  if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
};

type ArtifactEditorTarget = { workspaceId: string; artifactId: string; version: number };

const staleVersionMessageOf = (result: FetchResult): string | null => {
  if (!result.errors?.length) return null;
  const error = getApplicationError(new ApolloError({ graphQLErrors: result.errors }));
  return error?.code === ApplicationErrorCode.Conflict ? error.message : null;
};

/**
 * Save status and update handlers for editing an artifact. Agencies without the paged document
 * get the legacy editor's hook. Both run so the hook order is stable when the flag set changes.
 */
export const useArtifactEditor = (props: ArtifactEditorTarget) => {
  const paged = usePagedDocumentsEnabled();
  const legacy = useLegacyArtifactEditor(props);
  const current = usePagedArtifactEditor(props);
  return paged ? current : { ...legacy, staleVersionMessage: null };
};

const usePagedArtifactEditor = (props: ArtifactEditorTarget) => {
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('idle');
  const [staleVersionMessage, setStaleVersionMessage] = React.useState<string | null>(null);
  const artifactKey = `${props.workspaceId}:${props.artifactId}:${props.version}`;
  const currentArtifactKeyRef = React.useRef(artifactKey);
  currentArtifactKeyRef.current = artifactKey;
  const titleSaveGenerationRef = React.useRef(0);
  const titleSavedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // This hook stays mounted as a reader chooses versions, so status from the previous document
  // must not bleed into the new one.
  React.useEffect(() => {
    titleSaveGenerationRef.current += 1;
    if (titleSavedTimerRef.current) clearTimeout(titleSavedTimerRef.current);
    titleSavedTimerRef.current = null;
    setSaveStatus('idle');
    setStaleVersionMessage(null);
    return () => {
      titleSaveGenerationRef.current += 1;
      if (titleSavedTimerRef.current) clearTimeout(titleSavedTimerRef.current);
      titleSavedTimerRef.current = null;
    };
  }, [artifactKey]);

  const handleUpdate = React.useCallback(
    async (content: string) => {
      // Guard against invalid props
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      const result = await client.mutate<
        UpdateCaraWorkspaceArtifactMutation,
        UpdateCaraWorkspaceArtifactMutationVariables
      >({
        mutation: UpdateCaraWorkspaceArtifactDocument,
        variables: {
          workspaceId: props.workspaceId,
          artifactId: props.artifactId,
          version: props.version,
          input: {
            document: {
              content
            }
          }
        },
        context: editorSaveContext()
      });
      setStaleVersionMessage(staleVersionMessageOf(result));
      throwOnMutationErrors(result);
    },
    [props.workspaceId, props.artifactId, props.version]
  );

  const handleTitleUpdate = React.useCallback(
    async (title: string) => {
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      const targetArtifactKey = artifactKey;
      titleSaveGenerationRef.current += 1;
      const generation = titleSaveGenerationRef.current;
      if (titleSavedTimerRef.current) clearTimeout(titleSavedTimerRef.current);
      titleSavedTimerRef.current = null;
      setSaveStatus('saving');
      try {
        const result = await client.mutate<
          UpdateCaraWorkspaceArtifactMutation,
          UpdateCaraWorkspaceArtifactMutationVariables
        >({
          mutation: UpdateCaraWorkspaceArtifactDocument,
          variables: {
            workspaceId: props.workspaceId,
            artifactId: props.artifactId,
            version: props.version,
            input: {
              document: {
                title
              }
            }
          }
        });
        setStaleVersionMessage(staleVersionMessageOf(result));
        throwOnMutationErrors(result);
        if (
          currentArtifactKeyRef.current !== targetArtifactKey ||
          generation !== titleSaveGenerationRef.current
        ) {
          return;
        }
        setSaveStatus('saved');
        titleSavedTimerRef.current = setTimeout(() => {
          if (
            currentArtifactKeyRef.current === targetArtifactKey &&
            generation === titleSaveGenerationRef.current
          ) {
            setSaveStatus('idle');
          }
        }, 2000);
      } catch {
        if (
          currentArtifactKeyRef.current === targetArtifactKey &&
          generation === titleSaveGenerationRef.current
        ) {
          setSaveStatus('error');
        }
      }
    },
    [artifactKey, props.workspaceId, props.artifactId, props.version]
  );

  const handleVisibilityUpdate = React.useCallback(
    async (visibility: VisibilityConfigInput) => {
      if (!props.workspaceId || !props.artifactId || !props.version) {
        return;
      }

      const result = await client.mutate<
        UpdateCaraWorkspaceArtifactMutation,
        UpdateCaraWorkspaceArtifactMutationVariables
      >({
        mutation: UpdateCaraWorkspaceArtifactDocument,
        variables: {
          workspaceId: props.workspaceId,
          artifactId: props.artifactId,
          version: props.version,
          input: { visibility }
        }
      });
      throwOnMutationErrors(result);
    },
    [props.workspaceId, props.artifactId, props.version]
  );

  return {
    saveStatus,
    setSaveStatus,
    staleVersionMessage,
    handleUpdate,
    handleTitleUpdate,
    handleVisibilityUpdate
  };
};
